/**
 * Utilities for Medical Opinion text processing
 */

/**
 * Regular expression to match source citations in the format:
 * (source: [document name], page [X], date [YYYY-MM-DD])
 * Also handles variations like:
 * (source: [document name], page [X])
 * (source: [document name], pages [X-Y])
 */
const CITATION_REGEX = /\(source:\s*([^,]+),\s*pages?\s*(\d+)(?:-\d+)?(?:,\s*date\s*[\d-]+)?\)/gi;
const CITATION_STRIP_REGEX = /\((?:\s*(?:source|see)\s*:)(?:[^()]|\([^()]*\))*\)/gi;
const CITATION_STRIP_LINE_REGEX = /^\s*(?:[-*]\s*)?(?:source|see)\s*:[^\n]*$/gim;

/**
 * Parse citations from MO text
 * @param {string} text - The medical opinion text
 * @param {Array} contextFiles - Array of context files with fileId, fileName, topPages
 * @returns {Array} Array of citation objects with { text, fileName, pageNumber, fileId }
 */
export function parseCitations(text, contextFiles = []) {
  if (!text) return [];

  const citations = [];
  let match;

  // Reset regex lastIndex
  CITATION_REGEX.lastIndex = 0;

  while ((match = CITATION_REGEX.exec(text)) !== null) {
    const fullMatch = match[0];
    const documentName = match[1].trim();
    const pageNumber = parseInt(match[2], 10);

    // Try to find matching file in context files
    const matchingFile = contextFiles.find((file) => {
      const fileName = file.fileName || '';
      // Check if document name matches file name (partial match)
      return (
        fileName.toLowerCase().includes(documentName.toLowerCase()) ||
        documentName.toLowerCase().includes(fileName.toLowerCase().replace(/\.[^.]+$/, ''))
      );
    });

    citations.push({
      text: fullMatch,
      fileName: documentName,
      pageNumber,
      fileId: matchingFile?.fileId || null,
      index: match.index,
    });
  }

  return citations;
}

/**
 * Strip all citations from MO text for final output
 * @param {string} text - The medical opinion text with citations
 * @returns {string} Clean text without citations
 */
export function stripCitations(text) {
  if (!text) return '';

  // Remove citations and clean up any double spaces left behind
  return text
    .replace(CITATION_STRIP_REGEX, '')
    .replace(CITATION_STRIP_LINE_REGEX, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+\./g, '.')
    .replace(/\s+,/g, ',')
    .trim();
}

/**
 * Extract unique sources from citations for display
 * @param {Array} citations - Array of parsed citations
 * @returns {Array} Array of unique source objects { fileName, pages: [{ pageNumber, fileId }] }
 */
export function groupCitationsBySources(citations) {
  if (!citations || citations.length === 0) return [];

  const sourceMap = new Map();

  citations.forEach((citation) => {
    const key = citation.fileName.toLowerCase();
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        fileName: citation.fileName,
        fileId: citation.fileId,
        pages: [],
      });
    }

    const source = sourceMap.get(key);
    // Only add unique pages
    if (!source.pages.some((p) => p.pageNumber === citation.pageNumber)) {
      source.pages.push({
        pageNumber: citation.pageNumber,
        fileId: citation.fileId,
      });
    }
  });

  // Sort pages within each source
  sourceMap.forEach((source) => {
    source.pages.sort((a, b) => a.pageNumber - b.pageNumber);
  });

  return Array.from(sourceMap.values());
}

export default {
  parseCitations,
  stripCitations,
  groupCitationsBySources,
};
